import React, { useCallback, useEffect, useMemo } from 'react';
import { isNull, isUndefined } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { ConfirmationModal } from '../../shared/modal/ConfirmationModal';
import { Document } from '../../shared/icons';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import { getIsUploading, SecondaryUploadDocument, uploadDocumentStarted, uploadDocumentUpdateValue, toggleUploadModal, getAnalysisUploadDocument, SupportingUploadDocument, fetchLinkedDocumentsStarted, getOriginalDocument, getSecondaryDocument } from '../../documents/my-documents/store';
import { DocumentTypeSelector } from '../../documents/my-documents/upload/DocumentTypeSelector';
import { SecondaryDocument } from '../../documents/my-documents/upload/SecondaryDocument';
import { SupportDocument } from '../../documents/my-documents/upload/SupportDocument';
import { futureDate } from '../../../utils/luxon';
import { getUserHasFeaturePermission } from '../../auth/login/store';
import { FeaturePermission } from '../../admin/users/store';
import { isISDAFamilyAgreementType } from '../../admin/documents/store/typeAssertions';
import { PageLoadingDots } from '../../shared/loading-dots/PageLoadingDots';
import styles from './DatasetInstance.module.scss';
import { DocumentType } from '../../documents/store/types';

interface SecondaryDocumentModalProps {
    isOpen: boolean;
    disabled?: boolean;
    originalDocumentId: number;
    documentId: number;
    executedDate: string;
    agreementTypeId?: number | null;
}

export const SecondaryDocumentModal: React.FC<SecondaryDocumentModalProps> = ({ isOpen, disabled = false, originalDocumentId, documentId, executedDate, agreementTypeId }) => {
    const dispatch = useAppDispatch();
    const document = useAppSelector(getAnalysisUploadDocument);
    const originalDocument = useAppSelector(getOriginalDocument);
    const secondaryDocument = useAppSelector(getSecondaryDocument);
    const hasUploadDocumentPermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.UPLOAD_DOCUMENTS]));
    const isUploading = useAppSelector(getIsUploading);
    const { description, file } = document;

    const closeModal = useCallback(() => dispatch(toggleUploadModal(false)), [dispatch]);
    const uploadDocument = useCallback(() => dispatch(uploadDocumentStarted(true)), [dispatch]);

    const getDatedAsOf = useCallback((type: DocumentType) => {
        const isSupportDocumentType = type === DocumentType.SUPPORT;
        if (!isNull(secondaryDocument) && isSupportDocumentType) {
            return secondaryDocument.datedAsOf;
        }
        if (!isNull(originalDocument)) {
            return !isNull(originalDocument.datedAsOf) ? futureDate({ days: 1 }, originalDocument.datedAsOf) : null;
        }
        return null;
    }, [originalDocument, secondaryDocument]);

    const fetchLinkedDocuments = useCallback(() => {
        if (!isNull(agreementTypeId) && !isUndefined(agreementTypeId) && isISDAFamilyAgreementType(agreementTypeId)) {
            dispatch(fetchLinkedDocumentsStarted(originalDocumentId));
        }
    }, [agreementTypeId, dispatch, originalDocumentId]);

    useEffect(() => {
        fetchLinkedDocuments();
    }, [fetchLinkedDocuments]);

    const selectType = useCallback((type: DocumentType) => {
        const isSupportDocumentType = type === DocumentType.SUPPORT;
        const datedAsOf = getDatedAsOf(type);
        const baseDocumentId = isSupportDocumentType ? documentId : originalDocumentId;
        const newExecutedDate = isSupportDocumentType ? executedDate : futureDate({ days: 1 }, executedDate);
        dispatch(uploadDocumentUpdateValue('type', type));
        dispatch(uploadDocumentUpdateValue('originalDocumentId', baseDocumentId));
        dispatch(uploadDocumentUpdateValue('executedDate', newExecutedDate));
        dispatch(uploadDocumentUpdateValue('datedAsOf', datedAsOf));
    }, [dispatch, documentId, executedDate, getDatedAsOf, originalDocumentId]);

    const modalContent = useMemo(() => {
        if (isUploading) {
            return <PageLoadingDots />;
        }
        if (isUndefined(document.type)) {
            return <DocumentTypeSelector selectType={selectType} />;
        }
        if (document.type === DocumentType.SUPPORT) {
            return <SupportDocument uploadDocument={document as SupportingUploadDocument} disabled={disabled} />;
        }
        return <SecondaryDocument disabled={disabled} uploadDocument={document as SecondaryUploadDocument} />;
    }, [disabled, document, isUploading, selectType]);

    return (
        <ConfirmationModal
            isOpen={isOpen}
            closeModal={closeModal}
            confirmLabel='Upload'
            confirmDisabled={!file || !description || !hasUploadDocumentPermission}
            confirm={uploadDocument}
            testId='upload-secondary-document'
            showConfirm={!isUploading}
            showClose={!isUploading}
        >
            <div className={styles.uploadDocumentWrapper} data-testid='upload-secondary-document-modal-wrapper'>
                <ModalHeader
                    label='Upload a new document'
                    testId='upload-secondary-document-modal'
                    icon={Document}
                />
                {modalContent}
            </div>
        </ConfirmationModal>
    );
};
